import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import FilledButton from "../../components/button/FilledButton";
import LogoCircular from "../../components/logo/LogoCircle";
import { useUser } from "../../context/UserContext";
import { authService } from "../../service/AuthService";
import colors from "../../theme/color/ColorTheme";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${colors.backgroundPrimary};
`;

const Card = styled.div`
  width: 100%;
  max-width: 380px;
  padding: 20px;
`;

const Logo = styled.div`
  img {
    width: 160px;
    height: 160px;
  }
  margin: 0 auto 1.5rem auto;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  font-weight: light;
  color: ${colors.black};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  font-weight: 400;
  color: ${colors.textMuted};
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;
  transition: border 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.black};
  }
`;

const ErrorMsg = styled.p`
  font-size: 0.8rem;
  color: #d32f2f;
  margin-top: 10px;
`;

function LoginPage() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState();
  const { user, loading } = useUser();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    let newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      authService.login(
        formData,
        (res) => {
          window.location.href = "/admin/dashboard";
        },
        {
          401: () => {
            setErrorMessage("Invalid password or email. Please try again. ");
          },
        }
      );
    }
  };

  useEffect(() => {
    if (!loading && user?.token) {
      navigate("/admin/dashboard");
    }
  }, [loading, user, navigate]);

  return (
    <Wrapper>
      {loading ? (
        <>Loading...</>
      ) : (
        <>
          <Card>
            <Logo>
              <LogoCircular />
            </Logo>
            <Title> Login to your account.</Title>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              {/* Email */}
              <div>
                <Label>Username</Label>
                <Input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your username"
                />
                {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
              </div>

              {/* Password */}
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                />
                {errors.password && <ErrorMsg>{errors.password}</ErrorMsg>}
              </div>
              <ErrorMsg>{errorMessage}</ErrorMsg>
              <FilledButton
                text={"Login"}
                onClick={(e) => {
                  handleSubmit();
                }}
              />
            </Form>
          </Card>
        </>
      )}
    </Wrapper>
  );
}

export default LoginPage;
